import useEmblaCarousel from 'embla-carousel-react';
import React, { Suspense, useCallback, useEffect, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Skeleton } from '@savgroup-front-common/core/src/atoms/Skeleton';
import {
  SafeFormattedMessageWithoutSpread,
  safeFormattedIntlString,
} from '@savgroup-front-common/core/src/formatters';
import getPartnerScenarioTypeMessage from '@savgroup-front-common/core/src/formatters/getPartnerScenarioTypeMessage';
import { Accordion } from '@savgroup-front-common/core/src/molecules/Accordion';
import Field from '@savgroup-front-common/core/src/molecules/Field/Field';
import { SummarySection } from '@savgroup-front-common/core/src/molecules/SummarySection/SummarySection';
import { MessageType, PartnerScenarioTypes } from '@savgroup-front-common/types';

import { useDossierFileInfoContext } from '../../../../context/DossierFileInfoContext';
import DisplayActorName from '../../../DisplayActorName/DisplayActorName';
import { DOSSIER_RESPONSIVE_MAX_WIDTH_PX } from '../../../../pages/DossierPage.styles';
import getHolderType from '../../../../utils/getHolderType';
import { useGetFileSummaryNewUi, useGetFileSummaryNewUiWithClaim } from '../../FileSummaryNewUi.hooks';
import { messages } from '../../messages';
import { FileOwnerField } from '../FileOwnerField/FileOwnerField';
import { StoreActorField } from '../StoreActorField/StoreActorField';
import { WarrantyType } from '../WarrantyType/WarrantyType';

import {
  $CarouselCard,
  $CarouselDot,
  $CarouselDots,
  $CarouselSlide,
  $CarouselTrack,
  $CarouselViewport,
  $InlineField,
  $InlineFieldLabel,
  $InlineFieldValue,
  $ResponsiveFileSection,
  $SectionTitle,
} from './FileSectionSummary.styles';

interface InlineSummaryFieldProps {
  label: MessageType;
  children: ReactNode;
}

const InlineSummaryField = ({ label, children }: InlineSummaryFieldProps) => (
  <$InlineField>
    <$InlineFieldLabel>
      <SafeFormattedMessageWithoutSpread message={label} />
    </$InlineFieldLabel>
    <$InlineFieldValue>{children}</$InlineFieldValue>
  </$InlineField>
);

interface FileSectionSummaryProps {
  createdDate: Date;
  sellerId: string;
  owner: { id: string };
  siteActorId: string;
  storeActorId: string;
  siteActorType: string;
  actorId: string;
  actorType: string;
}

export const FileSectionSummary: React.FC<FileSectionSummaryProps> = ({
  createdDate,
  sellerId,
  owner,
  siteActorId,
  siteActorType,
  actorId,
  actorType,
  storeActorId,
}) => {
  const { file } = useDossierFileInfoContext();
  const claimId = file?.claimInfoSummary?.claimId;

  const { data } = useGetFileSummaryNewUiWithClaim(claimId);
  const fileSummary = data?.file;
  const ignoreDateRulesChecking = Boolean(data?.claim?.ignoreDateRulesChecking);

  const products = fileSummary?.context?.products ?? [];
  const context = fileSummary?.context;

  const firstProduct = products.at(0);
  const intl = useIntl();
  const { data: fileSummaryData } = useGetFileSummaryNewUi();
  const assignedUserName = fileSummaryData?.context.assignedUserName;
  const storeActorName = fileSummaryData?.context.storeActorName;
  const siteActorName = fileSummaryData?.context.siteActorName;

  const [isNarrowViewport, setIsNarrowViewport] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${DOSSIER_RESPONSIVE_MAX_WIDTH_PX}px)`).matches
      : false,
  );
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: false,
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${DOSSIER_RESPONSIVE_MAX_WIDTH_PX}px)`);
    const onChange = () => {
      setIsNarrowViewport(mediaQuery.matches);
    };

    onChange();
    mediaQuery.addEventListener('change', onChange);

    return () => {
      mediaQuery.removeEventListener('change', onChange);
    };
  }, []);

  useEffect(() => {
    if (!emblaApi) {
      return undefined;
    }

    const onSelect = () => {
      setSelectedSlideIndex(emblaApi.selectedScrollSnap());
    };

    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  const handleDotClick = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  const depositStoreName = fileSummary?.context?.depositStoreName;
  const isMultiProduct = products.length > 1;
  const shouldShowPartnerScenarioTypeTypeField =
    context?.partnerScenarioType &&
    context.partnerScenarioType !== PartnerScenarioTypes.NONE;
  const shouldShowHolderTypeField = Boolean(firstProduct?.holder);
  const holderLabel = shouldShowHolderTypeField
    ? [getHolderType(firstProduct.holder.holderType), firstProduct.holder.holderName || undefined]
        .filter((x) => !!x)
        .join(' - ')
    : undefined;

  if (isNarrowViewport) {
    return (
      <$ResponsiveFileSection>
        <$SectionTitle>
          <SafeFormattedMessageWithoutSpread message={messages.file} />
        </$SectionTitle>

        <$CarouselViewport ref={emblaRef}>
          <$CarouselTrack>
            <$CarouselSlide>
              <$CarouselCard>
                {createdDate && (
                  <InlineSummaryField label={messages.fileCreatedDate}>
                    {intl.formatDate(createdDate, {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })}
                  </InlineSummaryField>
                )}
                {firstProduct?.translations.warrantyTypeName && (
                  <InlineSummaryField label={messages.warrantyTypeName}>
                    <WarrantyType
                      name={firstProduct.translations.warrantyTypeName}
                      ignoreDateRulesChecking={ignoreDateRulesChecking}
                    />
                  </InlineSummaryField>
                )}
                {firstProduct?.translations.issueName && (
                  <InlineSummaryField label={messages.issueName}>
                    {firstProduct.translations.issueName}
                  </InlineSummaryField>
                )}
                {firstProduct?.translations.reasonName && (
                  <InlineSummaryField label={messages.reasonName}>
                    {firstProduct.translations.reasonName}
                  </InlineSummaryField>
                )}
                {firstProduct?.translations.solutionName && (
                  <InlineSummaryField label={messages.solutionName}>
                    {firstProduct.translations.solutionName}
                  </InlineSummaryField>
                )}
                {firstProduct?.description && (
                  <InlineSummaryField label={messages.description}>
                    {firstProduct.description}
                  </InlineSummaryField>
                )}
              </$CarouselCard>
            </$CarouselSlide>

            <$CarouselSlide>
              <$CarouselCard>
                {assignedUserName && (
                  <InlineSummaryField label={messages.fileOwner}>
                    {assignedUserName}
                  </InlineSummaryField>
                )}
                {actorId && (
                  <InlineSummaryField label={messages.actor}>
                    <DisplayActorName
                      actorId={actorId}
                      actorType={actorType}
                      sellerId={sellerId}
                      ownerId={owner.id}
                    />
                  </InlineSummaryField>
                )}
                {siteActorId && (
                  <InlineSummaryField label={messages.siteActor}>
                    {siteActorName || (
                      <DisplayActorName
                        actorId={siteActorId}
                        actorType={siteActorType}
                        sellerId={sellerId}
                        ownerId={owner.id}
                      />
                    )}
                  </InlineSummaryField>
                )}
                {storeActorId && storeActorName && (
                  <InlineSummaryField label={messages.storeActor}>
                    {storeActorName}
                  </InlineSummaryField>
                )}
                {depositStoreName && (
                  <InlineSummaryField label={messages.depositStore}>
                    {depositStoreName}
                  </InlineSummaryField>
                )}
                {holderLabel && (
                  <InlineSummaryField label={messages.holder}>{holderLabel}</InlineSummaryField>
                )}
                {shouldShowPartnerScenarioTypeTypeField && (
                  <InlineSummaryField label={messages.rmaType}>
                    {safeFormattedIntlString(
                      getPartnerScenarioTypeMessage(context.partnerScenarioType),
                    )}
                  </InlineSummaryField>
                )}
                {firstProduct?.reasonComment && (
                  <InlineSummaryField label={messages.reasonComment}>
                    {firstProduct.reasonComment}
                  </InlineSummaryField>
                )}
              </$CarouselCard>
            </$CarouselSlide>
          </$CarouselTrack>
        </$CarouselViewport>

        <$CarouselDots>
          {[0, 1].map((index) => (
            <$CarouselDot
              key={index}
              type="button"
              $isActive={selectedSlideIndex === index}
              onClick={() => handleDotClick(index)}
              aria-label={index === 0 ? 'Voir le résumé dossier' : 'Voir les détails dossier'}
              aria-current={selectedSlideIndex === index}
            />
          ))}
        </$CarouselDots>
      </$ResponsiveFileSection>
    );
  }

  return (
    <SummarySection title={messages.file}>
      {createdDate && (
        <Field
          label={messages.fileCreatedDate}
          copy={intl.formatDate(createdDate, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        >
          {intl.formatDate(createdDate, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
          })}
        </Field>
      )}
      {isMultiProduct &&
        products.map((product, index) => {
          return (
            <Accordion
              key={product.ownerProductId}
              initialIsOpen={index === 0}
              title={`${[product.brandDisplayName, product.modelDisplayName].join(' - ')}`}
            >
              {product.translations.warrantyTypeName && (
                <Field
                  label={messages.warrantyTypeName}
                  copy={product.translations.warrantyTypeName}
                >
                  <WarrantyType
                    name={product.translations.warrantyTypeName}
                    ignoreDateRulesChecking={ignoreDateRulesChecking}
                  />
                </Field>
              )}
              {product.translations.issueName && (
                <Field label={messages.issueName} copy={product.translations.issueName}>
                  {product.translations.issueName}
                </Field>
              )}
              {product.translations.reasonName && (
                <Field label={messages.reasonName} copy={product.translations.reasonName}>
                  {product.translations.reasonName}
                </Field>
              )}
              {product.translations.solutionName && (
                <Field label={messages.solutionName} copy={product.translations.solutionName}>
                  {product.translations.solutionName}
                </Field>
              )}
              {product.reasonComment && (
                <Field label={messages.reasonComment} copy={product.reasonComment}>
                  {product.reasonComment}
                </Field>
              )}
            </Accordion>
          );
        })}
      {!isMultiProduct && firstProduct && (
        <>
          {firstProduct.translations.warrantyTypeName && (
            <Field
              label={messages.warrantyTypeName}
              copy={firstProduct.translations.warrantyTypeName}
            >
              <WarrantyType
                name={firstProduct.translations.warrantyTypeName}
                ignoreDateRulesChecking={ignoreDateRulesChecking}
              />
            </Field>
          )}
          {firstProduct.translations.issueName && (
            <Field label={messages.issueName} copy={firstProduct.translations.issueName}>
              {firstProduct.translations.issueName}
            </Field>
          )}
          {firstProduct.translations.reasonName && (
            <Field label={messages.reasonName} copy={firstProduct.translations.reasonName}>
              {firstProduct.translations.reasonName}
            </Field>
          )}
          {firstProduct.reasonComment && (
            <Field
              label={messages.reasonComment}
              copy={firstProduct.reasonComment}
              isEllipsisEnabled={false}
              hasMaxWidth={true}
            >
              {firstProduct.reasonComment}
            </Field>
          )}
          {firstProduct.translations.solutionName && (
            <Field label={messages.solutionName} copy={firstProduct.translations.solutionName}>
              {firstProduct.translations.solutionName}
            </Field>
          )}
        </>
      )}
      {actorId && (
        <Field label={messages.actor}>
          <DisplayActorName
            actorId={actorId}
            actorType={actorType}
            sellerId={sellerId}
            ownerId={owner.id}
          />
        </Field>
      )}
      {siteActorId && (
        <Field label={messages.siteActor}>
          <DisplayActorName
            actorId={siteActorId}
            actorType={siteActorType}
            sellerId={sellerId}
            ownerId={owner.id}
          />
        </Field>
      )}
      <Suspense fallback={<Skeleton />}>
        {storeActorId && <StoreActorField />}
      </Suspense>
      {depositStoreName && (
        <Field label={messages.depositStore} copy={depositStoreName}>
          {depositStoreName}
        </Field>
      )}
      {shouldShowHolderTypeField && (
        <Field label={messages.holder} copy={holderLabel} isEllipsisEnabled={false}>
          {holderLabel}
        </Field>
      )}
      {shouldShowPartnerScenarioTypeTypeField && (
        <Field
          label={messages.rmaType}
          copy={safeFormattedIntlString(
            getPartnerScenarioTypeMessage(context.partnerScenarioType),
          )}
        >
          {getPartnerScenarioTypeMessage(context.partnerScenarioType)}
        </Field>
      )}
      <FileOwnerField />
    </SummarySection>
  );
};
